import {
  Flex,
  FormControl,
  FormLabel,
  ModalBody,
  ModalContent,
  ModalFooter,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ChainFullIds, ChainNamespaces, parseChainFullId } from "@umami/chains";
import { type TezTransfer } from "@umami/core";
import { useGetChainBalance } from "@umami/state";
import { FormProvider } from "react-hook-form";

import { AddressTile } from "../../AddressTile/AddressTile";
import { TezTile } from "../../AssetTiles/TezTile";
import { SafeSendTile } from "../../SafeSend/SafeSendTile";
import { SignButton } from "../SignButton";
import { SignPageHeader } from "../SignPageHeader";
import { type SignPageProps, getSender, useSignPageHelpers } from "../utils";

import { AsyncSignPageFee } from "@/components/SendFlow/SignPageFee";

export const SignPage = (props: SignPageProps) => {
  const { operations: initialOperations, chainId = ChainFullIds.TEZOS } = props;
  const { fee, operations, estimationFailed, isLoading, form, signer, onSign } = useSignPageHelpers(
    initialOperations,
    chainId
  );
  const isSafeSendEnabled = form.watch("isSafeSendEnabled");
  const safeSendFee = form.watch("safeSendFee");
  const displayFee = isSafeSendEnabled ? safeSendFee : fee;
  const { balance } = useGetChainBalance()(chainId);

  const hideBalance = useBreakpointValue({ base: true, md: false });
  const tezOperation = operations.operations[0] as TezTransfer;

  const { namespace } = parseChainFullId(chainId);

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form>
          <SignPageHeader />
          <ModalBody gap="24px">
            <FormControl>
              <FormLabel width="full">Amount</FormLabel>
              <TezTile chainId={chainId} mutezAmount={tezOperation.amount} />
              <Flex justifyContent="end" width="full" marginTop="12px">
                <AsyncSignPageFee chainId={chainId} fee={displayFee} />
              </Flex>
            </FormControl>
            <FormControl>
              <FormLabel width="full">From</FormLabel>
              <AddressTile address={getSender(operations, chainId)} hideBalance={hideBalance} />
            </FormControl>
            <FormControl>
              <FormLabel width="full">To</FormLabel>
              <AddressTile address={tezOperation.recipient} hideBalance={hideBalance} />
            </FormControl>
            {namespace === ChainNamespaces.TEZOS &&
              getSender(operations, chainId).pkh !== tezOperation.recipient.pkh && (
                <FormControl>
                  <Flex justifyContent="end" width="full" marginTop="12px">
                    <SafeSendTile balance={balance} operation={tezOperation} signer={signer} />
                  </Flex>
                </FormControl>
              )}
          </ModalBody>
          <ModalFooter marginTop="0px">
            <SignButton
              isDisabled={estimationFailed}
              isLoading={isLoading}
              onSubmit={onSign}
              signer={signer}
            />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
